import React, { useCallback, useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Play } from "lucide-react";

/* ===================== COMPONENTS ===================== */
import UploadBox from "../../components/ApiTryOut/UploadBox";
import ResponseBox from "../../components/ApiTryOut/ResponseBox";
import Input from "../../components/Input/Input";
import Notification from "../../components/Notification/Notification";

/* ===================== SERVICES & UTILS ===================== */
import { apiGet, apiPost } from "../../apiService";
import { getErrorMessage } from "../../utils/errorHelper";

/* ===================== STYLES ===================== */
import styles from "./ApiTryOut.module.scss";

/**
 * ApiTryOut Component
 * ------------------
 * - Renders dynamic API try-out form
 * - Supports Text & File inputs
 * - Converts File → Base64 for API
 * - Executes API with selected environment credentials
 */
const ApiTryOut = ({ isAdminView = false }) => {
  /* ===================== ROUTER HELPERS ===================== */
  const navigate = useNavigate();
  const location = useLocation();
  const { id: adminId } = useParams();

  const apiId = isAdminView ? adminId : location.state?.apiId;

  /* ===================== FILE UI STATE ===================== */
  const [fileState, setFileState] = useState({});

  /* ===================== CORE STATE ===================== */
  const [api, setApi] = useState(null);
  const [requestDetails, setRequestDetails] = useState([]);
  const [formData, setFormData] = useState({});
  const [response, setResponse] = useState(null);

  const [apiKeys, setApiKeys] = useState([]);
  const [selectedKeyId, setSelectedKeyId] = useState("");
  const [secret, setSecret] = useState(null);

  const [errors, setErrors] = useState({});
  const [notification, setNotification] = useState("");
  const [notificationType, setNotificationType] = useState("success");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [balance, setBalance] = useState(0);

  /* ===================== NOTIFICATION HELPER ===================== */
  const showNotification = (message, type = "success") => {
    setNotification(message);
    setNotificationType(type);
    setTimeout(() => setNotification(""), 3000);
  };

  /* ===================== FILE → BASE64 ===================== */
  const fileToBase64 = (file) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result.split(",")[1]);
      reader.onerror = reject;
    });

  const handleFileChange = async (fieldName, file, fieldType) => {
    if (!file) return;

    if (fieldType === "FileBase64") {
      const base64 = await fileToBase64(file);
      setFileState((prev) => ({ ...prev, [fieldName]: file }));
      setFormData((prev) => ({ ...prev, [fieldName]: base64 }));
    } else if (fieldType === "SingleFile" || fieldType === "MultipleFiles") {
      setFileState((prev) => ({ ...prev, [fieldName]: file }));
      setFormData((prev) => ({ ...prev, [fieldName]: file }));
    }

    setErrors((prev) => ({ ...prev, [fieldName]: "" }));
  };

  /* ===================== INIT FORM DATA ===================== */
  useEffect(() => {
    if (!requestDetails.length) return;

    const initial = {};
    requestDetails.forEach((f) => {
      initial[f.name] = f.value ?? "";
    });

    setFormData(initial);
  }, [requestDetails]);

  /* ===================== INPUT HANDLER ===================== */
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  /* ===================== VALIDATION ===================== */
  const validateRequestForm = () => {
    const newErrors = {};

    requestDetails.forEach((field) => {
      if (
        field.required === "Yes" &&
        (!formData[field.name] || formData[field.name].toString().trim() === "")
      ) {
        newErrors[field.name] = `${field.title} is required`;
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  /* ===================== FETCH SECRETS ===================== */
  const fetchSecrets = useCallback(async (keyId) => {
    try {
      const res = await apiGet("catalog", `/secrets/view-key/${keyId}`);
      const key = res?.data?.key;

      setSecret({
        clientKey: key.clientKey,
        clientSecret: key.clientSecret,
      });
    } catch {
      setSecret(null);
      showNotification("Unable to fetch API secrets", "error");
    }
  }, []);

  const fetchBalance = async () => {
    try {
      const res = await apiGet("catalog", "/apimaster/balance");
      if (res?.status === "success") {
        setBalance(Number(res.data?.balance || 0));
      }
    } catch (err) {
      console.error("Balance fetch error", err);
    }
  };

  useEffect(() => {
    if (!apiId) {
      navigate(isAdminView ? "/admin/admin-catalogue" : "/api-catalogue", {
        replace: true,
      });
      return;
    }

    apiGet("catalog", `/apimaster/tryout/create/${apiId}`)
      .then((res) => {
        const apiData = res?.data?.apis?.[0];
        const req = res?.data?.request_details || [];
        const keys = res?.data?.apiKeysDetail || [];

        if (!apiData) {
          navigate(isAdminView ? "/admin/admin-catalogue" : "/api-catalogue", {
            replace: true,
          });
          return;
        }

        setApi(apiData);
        setRequestDetails(req);
        setApiKeys(keys);

        if (keys.length > 0) {
          setSelectedKeyId(keys[0].id);
          fetchSecrets(keys[0].id);
        }
      })
      .catch(() => {
        navigate(isAdminView ? "/admin/admin-catalogue" : "/api-catalogue", {
          replace: true,
        });
      });
  }, [apiId, isAdminView, navigate, fetchSecrets]);

  /* ===================== SUBMIT API ===================== */
  const handleSubmit = async () => {
    if (isSubmitting) return;

    if (!validateRequestForm()) {
      showNotification("Please fill all required fields", "error");
      return;
    }

    if (!api?.code || !secret) {
      showNotification("API configuration missing", "error");
      return;
    }

    try {
      setIsSubmitting(true);
      const auth = btoa(`${secret.clientKey}:${secret.clientSecret}`);

      const hasFileUpload = requestDetails.some(
        (f) => f.data_type === "SingleFile" || f.data_type === "MultipleFiles",
      );

      let res;

      if (hasFileUpload) {
        const form = new FormData();
        form.append("api_code", api.code);

        requestDetails.forEach((field) => {
          const value = formData[field.name];
          if (!value) return;
          if (field.name === "api_code") return;

          if (field.data_type === "SingleFile") {
            form.append(field.name, value);
          } else if (field.data_type === "MultipleFiles") {
            value.forEach((file) => {
              form.append(field.name, file);
            });
          } else {
            form.append(field.name, value);
          }
        });

        res = await apiPost("catalog", "/v1/api-verify", form, {
          skipAuthRedirect: true,
          headers: {
            Authorization: `Basic ${auth}`,
          },
        });
      } else {
        const payload = {
          api_code: api.code,
          request_parameter: formData,
        };

        res = await apiPost("catalog", "/v1/api-verify", payload, {
          skipAuthRedirect: true,
          headers: {
            Authorization: `Basic ${auth}`,
            "Content-Type": "application/json",
          },
        });
      }

      setResponse(res?.data || res);
      showNotification("API executed successfully", "success");
    } catch (err) {
      const message = getErrorMessage(
        err?.response?.data || err,
        "API execution failed",
      );

      setResponse({
        httpStatus: err?.response?.status || "ERROR",
        data: message,
      });

      showNotification(message, "error");
    } finally {
      setIsSubmitting(false);
    }
  };

  /* ===================== RENDER ===================== */
  return (
    <div className={styles["admin-ac-api-try-out"]}>
      {/* NOTIFICATIONS */}
      {notification && (
        <div className={styles["admin-ac-api-try-out__toast-container"]}>
          <Notification message={notification} type={notificationType} />
        </div>
      )}

      {/* HEADER SECTION */}
      <div className={styles["admin-ac-api-try-out__header"]}>
        <button
          className={styles["admin-ac-api-try-out__back-btn"]}
          onClick={() => navigate(-1)}
          aria-label="Go Back"
        >
          <ArrowLeft size={20} strokeWidth={2.5} />
        </button>
        <div className={styles["admin-ac-api-try-out__title-group"]}>
          <h2 className={styles["admin-ac-api-try-out__title"]}>{api?.name}</h2>
          <p className={styles["admin-ac-api-try-out__subtitle"]}>
            {api?.introduction}
          </p>
        </div>
      </div>

      {/* MAIN FORM CARD */}
      <div className={styles["admin-ac-api-try-out__card"]}>
        {/* Environment Selection */}
        <div className={styles["admin-ac-api-try-out__form-group"]}>
          <Input
            label="Environment"
            type="select"
            value={selectedKeyId}
            onChange={(e) => {
              setSelectedKeyId(e.target.value);
              fetchSecrets(e.target.value);
            }}
            options={apiKeys.map((k) => ({
              label: `${k.keyName} (${k.serverType})`,
              value: k.id,
            }))}
            required
          />
        </div>

        {/* Dynamic Request Fields */}
        {requestDetails.map((field) => (
          <div
            className={styles["admin-ac-api-try-out__form-group"]}
            key={field.name}
          >
            <label className={styles["admin-ac-api-try-out__label"]}>
              {field.title}
              {field.required === "Yes" && (
                <span className={styles["admin-ac-api-try-out__required-star"]}>
                  {" "}
                  *
                </span>
              )}
            </label>

            {["File", "FileBase64", "SingleFile", "MultipleFiles"].includes(
              field.data_type,
            ) ? (
              <UploadBox
                file={fileState[field.name] || null}
                setFile={(file) =>
                  handleFileChange(field.name, file, field.data_type)
                }
                multiple={field.data_type === "MultipleFiles"}
              />
            ) : (
              <input
                type="text"
                placeholder={`Enter ${field.title}`}
                className={`${styles["admin-ac-api-try-out__input"]} ${
                  errors[field.name]
                    ? styles["admin-ac-api-try-out__input--error"]
                    : ""
                }`}
                name={field.name}
                value={formData[field.name] ?? ""}
                onChange={(e) => {
                  handleChange(e);
                  setErrors((prev) => ({
                    ...prev,
                    [field.name]: "",
                  }));
                }}
              />
            )}

            {errors[field.name] && (
              <div className={styles["admin-ac-api-try-out__error-text"]}>
                {errors[field.name]}
              </div>
            )}
          </div>
        ))}

        {/* Action Button */}
        <div className={styles["admin-ac-api-try-out__actions"]}>
          <button
            className={styles["admin-ac-api-try-out__btn"]}
            onClick={handleSubmit}
            disabled={isSubmitting}
          >
            <Play size={18} strokeWidth={2.5} />
            {isSubmitting ? "Executing..." : "Execute API"}
          </button>
        </div>
      </div>

      {/* API RESPONSE */}
      <ResponseBox response={response} />
    </div>
  );
};

export default ApiTryOut;
